import React, { createContext, useContext, useState, ReactNode } from "react";

type Language = "en" | "es";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Navigation
    "nav.home": "Home",
    "nav.quiz": "Find Your Path",
    "nav.chat": "Ask Our Guide",
    "nav.jobs": "Explore Jobs",
    "nav.jobsBoard": "Jobs Board",

    // Jobs Board Page
    "jobsBoard.title": "Forestry Jobs Board",
    "jobsBoard.subtitle": "Find open forestry and environmental positions",
    "jobsBoard.category.state": "State & County",
    "jobsBoard.category.universities": "Universities",
    "jobsBoard.category.conservation": "Conservation & Environment",
    "jobsBoard.category.general": "General Forestry Jobs",

    // Hero
    "hero.title": "Your Office Outdoors",
    "hero.title.part1": "If you worked outdoors,",
    "hero.title.accent": "this could be your office!",
    "hero.subtitle": "You don’t sit at it. You work in it.",
    "hero.cta": "Learn About Forestry Jobs",
    "hero.cta.primary": "Chat With Our Guide",
    "hero.cta.secondary": "Take Personality Quiz",
    "hero.secondary": "Chat With Our Guide",

    // Not an Office Section - Narrative lines
    "notOffice.line1": "Some days you’re working with maps.",
    "notOffice.line2": "Other days, your office is the river.",
    "notOffice.line3": "It's hands-on work that matters.",

    // Benefits
    "benefits.title": "Why work outdoors?",
    "benefits.subtitle": "Rewarding work. Lasting impact.",
    "benefits.skills.title": "Learn Real Skills",
    "benefits.skills.desc": "Master hands-on techniques used by professionals every day in the field.",
    "benefits.training.title": "Get Trained",
    "benefits.training.desc": "Access world-class training programs designed to launch your forestry career.",
    "benefits.paid.title": "Get Paid",
    "benefits.paid.desc": "Start earning while you learn with competitive wages from day one.",
    "benefits.growth.title": "Grow Over Time",
    "benefits.growth.desc": "Build a rewarding career with clear advancement paths and endless opportunities.",

    // Jobs
    "jobs.title": "You could be a...",
    "jobs.subtitle": "From tech-savvy to hands-on, there's a forest job for every personality.",
    "jobs.ranger.title": "Forester",
    "jobs.ranger.desc": "Gather information about forest health so you can predict and identify problems.",
    "jobs.scientist.title": "Forestry Technician",
    "jobs.scientist.desc": "Research ecosystems, track wildlife, and develop conservation strategies.",
    "jobs.tech.title": "GIS Specialist",
    "jobs.tech.desc": "Map forests using drones, satellites, and cutting-edge technology.",
    "jobs.silviculture.title": "Silviculture Technician",
    "jobs.silviculture.desc": "Grow healthier forests by planting, thinning, and managing tree stands.",
    "jobs.firefighter.title": "Wildland Firefighter",
    "jobs.firefighter.desc": "Be a hero protecting communities and ecosystems from wildfires.",
    "jobs.arborist.title": "Urban Arborist",
    "jobs.arborist.desc": "Bring the forest to the city by caring for urban trees.",
    "jobs.ranger.personality1": "Realistic",
    "jobs.ranger.personality2": "Investigative",
    "jobs.ranger.task1": "Wildlife monitoring",
    "jobs.ranger.task2": "Fire prevention",
    "jobs.scientist.personality1": "Investigative",
    "jobs.scientist.personality2": "Realistic",
    "jobs.scientist.task1": "Data analysis",
    "jobs.scientist.task2": "Field research",
    "jobs.tech.personality1": "Investigative",
    "jobs.tech.personality2": "Conventional",
    "jobs.tech.task1": "Satellite mapping",
    "jobs.tech.task2": "Drone operations",
    "jobs.silviculture.personality1": "Realistic",
    "jobs.silviculture.personality2": "Investigative",
    "jobs.silviculture.task1": "Tree planting",
    "jobs.silviculture.task2": "Forest thinning",

    // Quiz CTA
    "quizCta.title": "Discover Your Personality ",
    "quizCta.subtitle":
      "Find out which of the 6 personality profiles matches you and explore the forestry jobs perfect for you.",
    "quizCta.button": "Take the Personality Quiz",

    // Chat CTA
    "chatCta.title": "Got questions? Chat with our forestry guide",
    "chatCta.subtitle": "Our AI mentor knows everything about forestry jobs and can help you find your path.",
    "chatCta.button": "Start Chatting",

    // Footer
    "footer.tagline": "Your future is greener than you think.",
    "footer.powered":
      "Major funding for this project is provided by the Arizona Department of Forestry and Fire Management's Urban and Community Forestry Program and the USDA Forest Service. These institutions are equal opportunity providers.",
    "footer.cta.title": "Nature is calling, will you join?",

    // Quiz
    "quiz.title": "Discover Your Personality & Forestry Jobs",
    "quiz.subtitle": "Find out which forestry careers match your personality type.",
    "quiz.instructions":
      "Select the characteristics that describe you best from each category. You can choose as many as you want!",
    "quiz.start": "Let's Go!",
    "quiz.next": "Next",
    "quiz.back": "Back",
    "quiz.seeResults": "See My Results",
    "quiz.restart": "Take Quiz Again",
    "quiz.yourProfile": "Your Personality Profile",
    "quiz.chatNow": "Chat About These Careers",
    "quiz.exploreMore": "Explore More Careers",
    "quiz.selectTraits": "Select Your Characteristics",
    "quiz.selectTraitsDesc": "Check all the traits that describe you from each personality category.",
    "quiz.selected": "Traits selected",
    "quiz.selectAtLeastOne": "Select at least one trait to see your results",
    "quiz.scoreBreakdown": "Your Score Breakdown",
    "quiz.stemSkills": "Related STEM Skills",
    "quiz.greenJobs": "Forestry Jobs For You",

    // Chat
    "chat.title": "Forestry Jobs Guide",
    "chat.placeholder": "Ask me anything about forestry jobs...",
    "chat.send": "Send",
    "chat.thinking": "Thinking...",
    "chat.welcome":
      "Hey there! 🌲 I'm your Forestry Jobs Guide. I know all about working in the forest—from fighting wildfires to researching ecosystems. What would you like to explore?",
    "chat.suggestions.1": "What forestry jobs pay the best?",
    "chat.suggestions.2": "Do I need a degree?",
    "chat.suggestions.3": "What's a day like for a ranger?",
    "chat.resources.title": "Explore Resources:",
    "chat.resources.videos": "🎬 Videos",
    "chat.resources.podcasts": "🎧 Podcasts",
    "chat.resources.games": "🎮 Games",
    "chat.resources.reading": "📚 Reading",
    "chat.resources.jobBoards": "💼 Job Boards",
    "chat.resources.videos.prompt": "What forestry videos would you recommend to learn more?",
    "chat.resources.podcasts.prompt": "What are some good podcasts about forestry and conservation?",
    "chat.resources.games.prompt": "Are there any interactive games to learn about forestry?",
    "chat.resources.reading.prompt": "What books or articles should I read about forestry jobs?",
     "chat.resources.jobBoards.prompt": "Where can I find forestry job listings and opportunities?",
    "chat.jobsBoard": "Jobs Board",
    "chat.exploreJobs": "Explore forestry related jobs:",
    "chat.drawer.title": "Forest Jobs & Resources",
    "chat.drawer.jobs": "Forest Jobs",
    "chat.drawer.resources": "Resources",
    "chat.drawer.category.science": "Science & Environment",
    "chat.drawer.category.technical": "Technical & Operations",
    "chat.drawer.category.business": "Administration & Business",
    "chat.drawer.category.design": "Design & Creative",
    "chat.drawer.category.community": "Community & Relations",
  },
  es: {
    // Navigation
    "nav.home": "Inicio",
    "nav.quiz": "Encuentra Tu Camino",
    "nav.chat": "Pregunta a Nuestro Guía",
    "nav.jobs": "Explora los empleos forestales",
    "nav.jobsBoard": "Bolsa de Empleo",

    // Jobs Board Page
    "jobsBoard.title": "Bolsa de Empleos Forestales",
    "jobsBoard.subtitle": "Encuentra vacantes forestales y ambientales a nivel nacional",
    "jobsBoard.category.state": "Estado y Condado",
    "jobsBoard.category.universities": "Universidades",
    "jobsBoard.category.conservation": "Conservación y Medio Ambiente",
    "jobsBoard.category.general": "Empleos Forestales Generales",

    // Hero
    "hero.title": "Tu oficina al aire libre",
    "hero.title.part1": "Si trabajaras al aire libre,",
    "hero.title.accent": "¡esta podría ser tu oficina!",
    "hero.title.part2": "a tu oficina al aire libre!",
    "hero.subtitle": "Y los árboles, tus compañeros de trabajo.",
    "hero.cta": "Chatea sobre empleos forestal",
    "hero.cta.primary": "Chatea sobre empleos forestal",
    "hero.cta.secondary": "Quiz de Personalidad",
    "hero.secondary": "Habla con Nuestro Guía",

    // Not an Office Section - Narrative lines
    "notOffice.line1": "Algunos días planificas.",
    "notOffice.line2": "Otros días estás afuera mejorando las cosas.",
    "notOffice.line3": "Es trabajo práctico que importa.",
    "notOffice.line4": "Y sí, es un trabajo real.",

    // Benefits
    "benefits.title": "¿Por qué trabajar al aire libre?",
    "benefits.subtitle": "Un trabajo gratificante, con un impacto verdadero.",
    "benefits.skills.title": "Aprende habilidades reales",
    "benefits.skills.desc": "Domina técnicas prácticas usadas por profesionales cada día en el campo.",
    "benefits.training.title": "Recibe buena capacitación",
    "benefits.training.desc":
      "Accede a programas de capacitación de clase mundial diseñados para lanzar tu carrera forestal.",
    "benefits.paid.title": "La paga es buena",
    "benefits.paid.desc": "Comienza a ganar mientras aprendes con salarios competitivos desde el primer día.",
    "benefits.growth.title": "Crece con el tiempo",
    "benefits.growth.desc": "Construye una carrera gratificante con claras rutas de avance y oportunidades infinitas.",

    // Jobs
    "jobs.title": "Descubre los trabajos forestales",
    "jobs.subtitle": "Desde tecnología hasta trabajo manual, hay un empleo forestal para cada personalidad.",
    "jobs.ranger.title": "Guardabosques",
    "jobs.ranger.desc": "Protege y administra tierras forestales mientras conectas visitantes con la naturaleza.",
    "jobs.scientist.title": "Científico Forestal",
    "jobs.scientist.desc": "Investiga ecosistemas, rastrea vida silvestre y desarrolla estrategias de conservación.",
    "jobs.tech.title": "Especialista en GIS",
    "jobs.tech.desc": "Mapea bosques usando drones, satélites y tecnología de punta.",
    "jobs.silviculture.title": "Técnico en Silvicultura",
    "jobs.silviculture.desc": "Cultiva bosques más saludables plantando, aclarando y manejando rodales de árboles.",
    "jobs.firefighter.title": "Bombero Forestal",
    "jobs.firefighter.desc": "Sé un héroe protegiendo comunidades y ecosistemas de incendios forestales.",
    "jobs.arborist.title": "Arborista Urbano",
    "jobs.arborist.desc": "Lleva el bosque a la ciudad cuidando árboles urbanos.",
    "jobs.ranger.personality1": "Realista",
    "jobs.ranger.personality2": "Investigador",
    "jobs.ranger.task1": "Monitoreo de fauna",
    "jobs.ranger.task2": "Prevención de incendios",
    "jobs.scientist.personality1": "Investigador",
    "jobs.scientist.personality2": "Realista",
    "jobs.scientist.task1": "Análisis de datos",
    "jobs.scientist.task2": "Investigación de campo",
    "jobs.tech.personality1": "Investigador",
    "jobs.tech.personality2": "Convencional",
    "jobs.tech.task1": "Mapeo satelital",
    "jobs.tech.task2": "Operación de drones",
    "jobs.silviculture.personality1": "Realista",
    "jobs.silviculture.personality2": "Investigador",
    "jobs.silviculture.task1": "Plantación de árboles",
    "jobs.silviculture.task2": "Aclareo forestal",

    // Quiz CTA
    "quizCta.title": "Descubre tu perfil de personalidad",
    "quizCta.subtitle":
      "¿Eres un productori, pensador, creador o líder? Descubre cuál de los 6 perfiles de personalidad te define y explora los trabajos forestales perfectas para ti.",
    "quizCta.button": "Haz el test de personalidad",

    // Chat CTA
    "chatCta.title": "¿Tienes Preguntas? Platica con nuestro Guía",
    "chatCta.subtitle":
      "Nuestro mentor impulsado por IA sabe todo sobre carreras y trabajos forestales. Él puede ayudarte a encontrar tu camino.",
    "chatCta.button": "Empezar a chatear",

    // Footer
    "footer.tagline": "Tu futuro es más verde de lo que piensas.",
    "footer.powered": "Impulsado por Project Learning Tree y Corazón Latino",
    "footer.cta.title": "La naturaleza te llama, ¿te unes?",

    // Quiz
    "quiz.title": "Descubre tu personalidad y los empleos forestales",
    "quiz.subtitle": "Descubre qué carreras forestales coinciden con tu tipo de personalidad.",
    "quiz.instructions":
      "Selecciona las características que mejor te describen de cada categoría. ¡Puedes elegir tantas como quieras!",
    "quiz.start": "¡Vamos!",
    "quiz.next": "Siguiente",
    "quiz.back": "Atrás",
    "quiz.seeResults": "Ver Mis Resultados",
    "quiz.restart": "Tomar Quiz de Nuevo",
    "quiz.yourProfile": "Tu Perfil de Personalidad",
    "quiz.chatNow": "Chatear Sobre Estas Carreras",
    "quiz.exploreMore": "Explorar Más Carreras",
    "quiz.selectTraits": "Selecciona Tus Características",
    "quiz.selectTraitsDesc": "Marca todas las características que te describen de cada categoría de personalidad.",
    "quiz.selected": "Características seleccionadas",
    "quiz.selectAtLeastOne": "Selecciona al menos una característica para ver tus resultados",
    "quiz.scoreBreakdown": "Tu Desglose de Puntuación",
    "quiz.stemSkills": "Habilidades STEM Relacionadas",
    "quiz.greenJobs": "Empleos Verdes Para Ti",

    // Chat
    "chat.title": "Guía de Trabajos Forestales",
    "chat.placeholder": "Pregúntame lo que quieras sobre trabajos forestales...",
    "chat.send": "Enviar",
    "chat.thinking": "Pensando...",
    "chat.welcome":
      "¡Hola! 🌲 Soy tu Guía de Trabajos Forestales. Sé todo sobre empleos en el bosque; desde combatir incendios hasta investigar ecosistemas. ¿Qué te gustaría explorar?",
    "chat.suggestions.1": "¿Qué trabajos forestales pagan mejor?",
    "chat.suggestions.2": "¿Necesito un título universitario?",
    "chat.suggestions.3": "¿Cómo es un día típico de un guardabosques?",
    "chat.resources.title": "Explora Recursos:",
    "chat.resources.videos": "🎬 Videos",
    "chat.resources.podcasts": "🎧 Podcasts",
    "chat.resources.games": "🎮 Juegos",
    "chat.resources.reading": "📚 Lecturas",
    "chat.resources.jobBoards": "💼 Bolsas de trabajo",
    "chat.resources.videos.prompt": "¿Qué videos sobre forestería me recomiendas?",
    "chat.resources.podcasts.prompt": "¿Cuáles son buenos podcasts sobre forestería y conservación?",
    "chat.resources.games.prompt": "¿Hay juegos interactivos para aprender sobre forestería?",
    "chat.resources.reading.prompt": "¿Qué libros o artículos debería leer sobre trabajos forestales?",
     "chat.resources.jobBoards.prompt": "¿Dónde puedo encontrar ofertas de trabajo en forestería?",
    "chat.jobsBoard": "Bolsa de Empleo",
    "chat.exploreJobs": "Explora trabajos forestales:",
    "chat.drawer.title": "Trabajos y Recursos Forestales",
    "chat.drawer.jobs": "Trabajos Forestales",
    "chat.drawer.resources": "Recursos",
    "chat.drawer.category.science": "Ciencia y Medio Ambiente",
    "chat.drawer.category.technical": "Técnico y Operaciones",
    "chat.drawer.category.business": "Administración y Negocios",
    "chat.drawer.category.design": "Diseño y Creatividad",
    "chat.drawer.category.community": "Comunidad y Relaciones",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>("en");

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return <LanguageContext.Provider value={{ language, setLanguage, t }}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
